/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState, useEffect } from "react";
import { Alert } from "@/types";
import Skeleton from "react-loading-skeleton";
import { useInput } from "@/hooks";
import { capitalize, makeTx, reduceString } from "@/utils";
import {
  Badge,
  TargetAddress,
  TableSortIcon,
  Checkbox,
  Button,
  TableCheckbox,
} from "@/components";
import { FaArchive } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";
import { TiWarning } from "react-icons/ti";
import { RiInboxUnarchiveFill } from "react-icons/ri";
import classNames from "classnames";
import { readAlertById } from "@/api";
import { useMonitor } from "@/contexts";
import { formatDistanceToNow } from "date-fns";
import { useMutation } from "react-query";
import { readAlerts, archiveAlerts, archiveAlertById } from "@/api";
import { uniq, without, intersection } from "lodash";
import { toast } from "react-toastify";

import "./style.scss";

interface Props {
  data: Array<Alert>;
  isLoading?: boolean;
  handlePagination?: (pagination: number) => void;
  onRefetch?(): void;
  sortBy: string;
  sortOrder: -1 | 1;
  requestSort(key: string): void;
  total: number;
  pageSize: number;
  idData: string[];
}

export const AlertTable = ({
  data,
  isLoading,
  onRefetch = () => {},
  sortBy,
  sortOrder,
  requestSort,
  total,
  idData,
}: Props) => {
  const { currentAddress, refetchNewAlertCount } = useMonitor();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selectAll = useInput(false);

  const { mutate: bulkRead, isLoading: isReading } = useMutation(
    () => readAlerts(selectedIds, true),
    {
      onSuccess: () => {
        setSelectedIds([]);
        onRefetch();
        refetchNewAlertCount();
      },
      onError: () => {},
    },
  );

  const { mutate: bulkArchive, isLoading: isArchiving } = useMutation(
    () => archiveAlerts(selectedIds, true),
    {
      onSuccess: () => {
        setSelectedIds([]);
        onRefetch();
        refetchNewAlertCount();
      },
      onError: () => {},
    },
  );
  const { mutate: bulkUnarchive, isLoading: isUnarchiving } = useMutation(
    () => archiveAlerts(selectedIds, false),
    {
      onSuccess: () => {
        setSelectedIds([]);
        onRefetch();
        refetchNewAlertCount();
      },
      onError: () => {},
    },
  );

  const headers = useMemo(
    () => [
      { headerName: "Bot Name", field: "botName", sortable: true },
      { headerName: "Detail", field: "detail", sortable: false },
      { headerName: "Severity", field: "severity", sortable: true },
      { headerName: "Date", field: "time", sortable: true },
    ],
    [],
  );

  const handleDelete = async (b: Alert) => {
    await archiveAlertById(b.id, !b?.isArchived);
    onRefetch();
    toast.success(<ToastMessage alert={b} onUndo={onRefetch} />);
  };

  const handleSelectRow = (id: string) => {
    let temp = [];
    if (selectedIds.includes(id)) {
      temp = selectedIds.filter((i) => i !== id);
    } else {
      temp = uniq([...selectedIds, id]);
    }
    setSelectedIds(temp);
  };

  const handleSelectAll = (val: boolean) => {
    if (val === true) {
      setSelectedIds(uniq([...selectedIds, ...data.map((alert) => alert.id)]));
    } else if (val === false) {
      setSelectedIds(without(selectedIds, ...data.map((alert) => alert.id)));
    }
  };

  useEffect(() => {
    const selected = intersection(
      selectedIds,
      data.map((alert) => alert.id),
    );

    if (selected.length === data.length) {
      selectAll.setValue(true);
    } else if (selected.length > 0) {
      selectAll.setValue("minus");
    } else {
      selectAll.setValue(false);
    }
    return () => {};
  }, [data, selectedIds]);

  const handleSelectAllAlerts = () => {
    setSelectedIds(idData);
  };
  const handleClearSelectAllAlert = () => {
    setSelectedIds([]);
  };

  return isLoading ? (
    <div className="loading">
      <Skeleton
        count={15}
        style={{ height: 26, marginBottom: 4, marginTop: 4 }}
      />
    </div>
  ) : data.length > 0 ? (
    <div className="alert-table-container">
      <table className="alert-table">
        <thead className="bot-table-header">
          {selectedIds.length > 0 && (
            <tr>
              <td colSpan={6} style={{ padding: 0 }}>
                <div className="d-flex alert-table-container__actions">
                  <div className="alert-table-container__actions-text">
                    <div className="my-auto">
                      {selectedIds.length} alerts selected
                    </div>
                    {selectedIds.length < total && (
                      <div
                        className="alert-table-container__actions-text-all"
                        onClick={handleSelectAllAlerts}
                      >{`Select all ${total} alerts`}</div>
                    )}
                    {/* {isSelectAllPages && ( */}
                    <div
                      className="alert-table-container__actions-text-all"
                      onClick={handleClearSelectAllAlert}
                    >
                      Clear Selection
                    </div>
                    {/* )} */}
                  </div>
                  <div className="alert-table-container__actions-buttons">
                    <Button
                      size="small"
                      className="ms-auto"
                      onClick={() => bulkRead()}
                      isLoading={isReading}
                      disabled={isArchiving || isUnarchiving || isReading}
                    >
                      Mark as read
                    </Button>
                    <Button
                      size="small"
                      className="ms-3"
                      onClick={() => bulkArchive()}
                      isLoading={isArchiving}
                      disabled={isArchiving || isUnarchiving || isReading}
                    >
                      Archive
                    </Button>
                    <Button
                      size="small"
                      className="ms-3"
                      onClick={() => bulkUnarchive()}
                      isLoading={isUnarchiving}
                      disabled={isArchiving || isUnarchiving || isReading}
                    >
                      Unarchive
                    </Button>
                  </div>
                </div>
              </td>
            </tr>
          )}
          <tr>
            <th>
              <TableCheckbox
                className="my-auto"
                value={selectAll.value}
                onChange={handleSelectAll}
              />
            </th>
            {headers.map((header, index) => (
              <th
                key={header.headerName}
                className={classNames({
                  sortable: header.sortable,
                  separator: index !== 0,
                })}
                onClick={() => header.sortable && requestSort(header.field)}
              >
                <div className="d-flex align-items-center">
                  <div>{header.headerName}</div>

                  {header.sortable && (
                    <div className="ms-1">
                      <TableSortIcon
                        status={sortBy === header.field ? sortOrder : 0}
                      />
                    </div>
                  )}
                </div>
              </th>
            ))}
            <th></th>
          </tr>
        </thead>

        <tbody>
          {data.map((item) => (
            <Row
              data={item}
              key={item.id}
              onDelete={() => handleDelete(item)}
              isSelected={selectedIds.includes(item.id)}
              onSelect={() => handleSelectRow(item.id)}
            />
          ))}
        </tbody>
      </table>
      {/* <DeleteAlertModal
        alert={selectedAlert!}
        {...deleteModal}
        onDelete={onRefetch}
      /> */}
    </div>
  ) : (
    <div className="text-center my-4 alert-table-empty">
      {currentAddress
        ? "No alerts for this address"
        : "No alerts for this project"}
    </div>
  );
};
interface ToastMessageProps {
  alert: Alert;
  onUndo?(): void;
}

const ToastMessage = ({ alert, onUndo = () => {} }: ToastMessageProps) => {
  const handleUndo = async () => {
    await archiveAlertById(alert.id, alert.isArchived);
    onUndo();
    toast.success(
      alert.isArchived
        ? "Alert unarchive is undone!"
        : "Alert archive is undone!",
    );
  };
  return (
    <div className="alert-archive-message">
      <div className="toast-message__icon">
        <i className="fa fa-check-circle"></i>
      </div>
      <div className="toast-message__text">
        {alert.isArchived ? "Alert is unarchived!" : "Alert is archived!"}
      </div>
      <button className="alert-archive-message__undo" onClick={handleUndo}>
        Undo
      </button>
    </div>
  );
};
interface RowProps {
  data: Alert;
  onDelete(): void;
  onSelect(): void;
  isSelected?: boolean;
}
const Row = ({
  data,
  onDelete = () => {},
  onSelect = () => {},
  isSelected = false,
}: RowProps) => {
  const { template, args, tx, targets, detail } = data;
  const [isRead, setIsRead] = useState(data.isRead);
  const { refetchNewAlertCount, currentAlertId, setCurrentAlertId } =
    useMonitor();
  const [isExpanded, setIsExpanded] = useState(currentAlertId === data.id);

  useEffect(() => {
    setIsRead(data.isRead);

    return () => {};
  }, [data.isRead]);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  const handleClick = () => {
    if (isExpanded) {
      setCurrentAlertId("");
    } else {
      setCurrentAlertId(data.id);
    }
    setIsExpanded((state) => !state);
    if (!isRead) {
      init();
    }
  };

  const init = async () => {
    await readAlertById(data.id, true);
    setIsRead(true);
    refetchNewAlertCount();
  };

  return (
    <>
      <tr
        className={classNames("alert-table-row", {
          "new-alert": !isRead,
          selected: isSelected,
        })}
        onClick={() => handleClick()}
      >
        <td onClick={(e) => e.stopPropagation()}>
          <Checkbox
            className="my-auto"
            value={isSelected}
            onChange={() => onSelect()}
          />
        </td>
        <td>
          <div>{data?.botName}</div>
        </td>
        <td>
          <div>{data?.detail}</div>
        </td>
        <td>
          <div className="d-flex">
            <Badge
              color={
                data.severity === "critical"
                  ? "dark-red"
                  : data.severity === "high"
                    ? "red"
                    : data.severity === "medium"
                      ? "yellow"
                      : data.severity === "low"
                        ? "light-yellow"
                        : "gray"
              }
              label={capitalize(data.severity)}
              className="me-auto my-auto d-flex justify-content-center align-items-center"
              width={60}
            />
          </div>
        </td>
        <td>
          <div className="wrapper">
            {`${formatDistanceToNow(new Date(data.created_at))} ago`}
          </div>
        </td>

        <td>
          <div className="icon-btn" onClick={handleDelete} title="Archive">
            {data.isArchived ? (
              <RiInboxUnarchiveFill color="#f79c05" size={16} />
            ) : (
              <FaArchive color="#9d1e1b" />
            )}
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={6}>
            <div className="detail-row">
              <div className="alert-detail d-flex">
                <TiWarning className="my-auto me-3" size={24} color="#F79C05" />
                <div className="flex-1 my-auto">{detail}</div>
              </div>
              <div className="alert-data-container mt-3">
                <div className="alert-data-cell">
                  <div className="alert-data-cell-title">Bot Type</div>
                  <div className="mt-0 mt-sm-2 alert-data-cell-value">
                    {template}
                  </div>
                </div>
                <div className="alert-data-cell">
                  <div className="alert-data-cell-title">Transactions</div>
                  <div className="mt-0 mt-sm-2 alert-data-cell-value">
                    <div className="d-block">
                      {(tx || []).map((oneTx, index) => {
                        const explorerURL = makeTx(
                          data.chain,
                          data.network,
                          oneTx,
                        );
                        return (
                          <div className="ms-auto" key={`key${index}`}>
                            {explorerURL ? (
                              <a
                                href={explorerURL}
                                title="View on blockchain explorer"
                                target="_blank"
                                rel="noreferrer"
                                className="d-flex"
                              >
                                {reduceString(oneTx, 5)}
                                <span className="d-flex my-auto ms-1">
                                  <FiExternalLink size={12} />
                                </span>
                              </a>
                            ) : (
                              reduceString(oneTx, 5)
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {Object.entries(args).map(([name, value]: any, index) => {
                  return (
                    <div className="alert-data-cell" key={`key${index}`}>
                      <div className="alert-data-cell-title">
                        {capitalize(name)}
                      </div>
                      <div className="mt-0 mt-sm-2 alert-data-cell-value">
                        {value}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="d-flex mt-3">
                {targets.map((target, index) => {
                  return (
                    <TargetAddress
                      address={target}
                      className="me-3"
                      key={`key${index}`}
                    />
                  );
                })}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
